import { useLearningTreeReviewEnabled } from "@utils/learningTreeReview";
import { nonNullable } from "@utils/utils";
import React, { useEffect, useMemo, useRef } from "react";
import { useBlocker, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Dialog,
  Headline,
  Label,
  Paragraph,
  Small,
  TabNavigation,
  Tooltip,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  EhrGoal,
  ReportStatusExtendedEnums,
  useCrLearningTreeQuery,
  useCreateReassessmentReportGoalsMutation,
  useGetReassessmentEhrGoalsQuery,
  useLearnerManualGoalSyncByIdQuery,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { EhrGoalRow } from "@pages/ClinicalReportDetails/SelectProgramming/EhrGoalRow";
import { SelectProgrammingFilterBar } from "@pages/ClinicalReportDetails/SelectProgramming/SelectProgrammingFilterBar";
import { useSelectProgramming } from "@pages/ClinicalReportDetails/SelectProgramming/useSelectProgramming";
import { useSelectProgrammingFilters } from "@pages/ClinicalReportDetails/SelectProgramming/useSelectProgrammingFilters";
import { useAdminData } from "@providers/AdminDataProvider";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

export const SelectProgramming: React.FC = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { reportId: reportIdFromUrl } = useParams();
  const { isGasEnabled } = useClinicalReportDetailsData();
  const { report } = useReport();
  const { isEhrSyncCleared } = useAdminData();
  const { captureGoalToggled, captureGoalsUpdated, captureEhrRefreshClicked } = useEhrAnalytics();

  const reportId = report.id ?? reportIdFromUrl ?? "";

  const { data, isLoading, isFetching, isError } = useGetReassessmentEhrGoalsQuery(
    { reportId },
    {
      enabled: !!reportId,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    },
  );

  const { state, toggleExpandGoal, toggleGoals, toggleTarget } = useSelectProgramming(data?.reassessmentEhrGoals);

  // Same managed-onboarding gate as the upload step's hand-off. Each click runs
  // a full EHR scrape, so only offer it on a settled empty answer: a cached
  // empty list still being refetched (e.g. right after an import finished) or a
  // failed load says nothing about the client's goals. While an import is
  // running FDM refuses a second one, so the trip would be pointless.
  const canRefreshFromEhr =
    isEhrSyncCleared &&
    !isFetching &&
    !isError &&
    report.extendedStatus !== ReportStatusExtendedEnums.ImportingClientData;

  const {
    searchTerm,
    setSearchTerm,
    setDateRangeFilter,
    customDateRange,
    setCustomDateRange,
    setStatusFilter,
    sortBy,
    setSortBy,
    activeTab,
    setActiveTab,
    filteredGoals,
  } = useSelectProgrammingFilters(state);

  const justSavedRef = useRef(false);
  // The redirect to the learning tree review isn't the user leaving with
  // unsaved picks: it can fire while the selection is still initializing.
  const reviewRedirectRef = useRef(false);

  const { mutate: createGoalsMutation, isPending: isCreatingGoals } = useCreateReassessmentReportGoalsMutation({
    onSuccess: async (data) => {
      if (!data.createReassessmentReportGoals?.status) {
        notifyError(data.createReassessmentReportGoals?.message ?? "Failed to update goals");
        return;
      }
      notifySuccess("Goals updated");
      const selectedGoals = state.filter((g) => g.checked);
      const targetCount = selectedGoals.reduce(
        (count, goal) => count + goal.targets.filter((t) => t.checked).length,
        0,
      );
      captureGoalsUpdated(selectedGoals.length, targetCount);
      justSavedRef.current = true;
      navigate("../report-page");
    },
    onError: () => {
      notifyError("Failed to update goals");
    },
  });

  const handleUpdate = async () => {
    const selectedGoals = state.filter((g) => g.checked);
    if (selectedGoals.length === 0) {
      notifyError("Please select at least one goal");
      return;
    }

    createGoalsMutation({
      goals: selectedGoals.map((goal) => ({
        goalId: goal.goalId,
        reportDomainId: !goal.reportGoalCreated ? locationState?.domainId : undefined,
        targetExclusions: isGasEnabled
          ? []
          : goal.targets.map((target) => ({
              targetId: target.targetId,
              excludeFromReport: !target.checked,
            })),
      })),
      reportId,
    });
  };

  // Compute initial checked state from raw server data (mirrors createInitialState logic)
  const initialChecked = useMemo(() => {
    const goals = (data?.reassessmentEhrGoals ?? []).filter(nonNullable) as EhrGoal[];
    const haveCheckedPreviously = goals.some((g) => g.reportGoalCreated);
    const goalIds = new Set<string>();
    const targetIds = new Set<string>();
    goals.forEach((goal) => {
      const goalChecked = haveCheckedPreviously ? goal.reportGoalCreated : true;
      if (goalChecked) {
        goalIds.add(goal.goalId);
        (goal.ehrTargets ?? []).filter(nonNullable).forEach((target) => {
          if (!target.reportTargetExcluded) targetIds.add(target.targetId);
        });
      }
    });
    return { goalIds, targetIds };
  }, [data]);

  const isDirty = useMemo(() => {
    const currentGoalIds = new Set(state.filter((g) => g.checked).map((g) => g.goalId));
    const currentTargetIds = new Set(
      state.filter((g) => g.checked).flatMap((g) => g.targets.filter((t) => t.checked).map((t) => t.targetId)),
    );
    if (currentGoalIds.size !== initialChecked.goalIds.size) return true;
    for (const id of currentGoalIds) {
      if (!initialChecked.goalIds.has(id)) return true;
    }
    if (currentTargetIds.size !== initialChecked.targetIds.size) return true;
    for (const id of currentTargetIds) {
      if (!initialChecked.targetIds.has(id)) return true;
    }
    return false;
  }, [state, initialChecked]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !justSavedRef.current &&
      !reviewRedirectRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Scroll to specific goal when navigating from "Manage EHR targets"
  const goalRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrolledRef = useRef(false);
  useEffect(() => {
    const scrollToGoalId = locationState?.scrollToGoalId;
    if (!scrollToGoalId || scrolledRef.current || !state.length) return;
    const el = goalRowRefs.current.get(scrollToGoalId);
    if (el) {
      scrolledRef.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [state, locationState?.scrollToGoalId]);

  const totalGoalCount = state.length;
  const selectedGoalCount = state.filter((g) => g.checked).length;

  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectableFiltered = filteredGoals.filter((g) => !g.disabled);
  const allVisibleChecked = selectableFiltered.length > 0 && selectableFiltered.every((g) => g.checked);
  const someVisibleChecked = selectableFiltered.some((g) => g.checked);
  const isIndeterminate = someVisibleChecked && !allVisibleChecked;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  // INT-148: an empty list for a learner whose programming was never imported
  // isn't "no goals", it's a sync that never ran (e.g. the EHR login dialog
  // failed before the import was queued). With no import row the report's
  // extendedStatus reads as DRAFT, so reopening the report lands here instead
  // of on refreshing-data. Send it there, which starts the import itself. A
  // learner with any sync status (in progress, failed, completed) stays here.
  const learnerId = report?.learner?.id || "";
  const {
    data: syncStatusData,
    isFetchedAfterMount: isSyncStatusFresh,
    isSuccess: isSyncStatusLoaded,
  } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    { enabled: !!learnerId && isEhrSyncCleared, refetchOnMount: "always" },
  );
  // isFetchedAfterMount also counts a failed fetch, and a failed fetch has no
  // status to read, so require a successful answer before calling it "never".
  const isNeverSynced = isSyncStatusFresh && isSyncStatusLoaded && !syncStatusData?.learnerById?.manualGoalSyncStatus;
  const hasNoEhrGoals = !isLoading && !!data && !data.reassessmentEhrGoals?.length;
  useEffect(() => {
    if (hasNoEhrGoals && isNeverSynced) {
      // Carry the location state through so refreshing-data hands it back
      // (e.g. returnToReportPage for the header's Back button).
      void navigate("../refreshing-data", { replace: true, state: locationState });
    }
  }, [hasNoEhrGoals, isNeverSynced, navigate, locationState]);

  // CR learning tree review: a first import stops for review before any data
  // is fetched, and a refresh can leave new items waiting for an answer.
  const learningTreeReviewEnabled = useLearningTreeReviewEnabled();
  const manualSyncStatus = syncStatusData?.learnerById?.manualGoalSyncStatus;
  const awaitingReview = manualSyncStatus === ApiLearnerManualGoalSyncStatusChoices.AwaitingReview;
  const hasWaitingItems = manualSyncStatus === ApiLearnerManualGoalSyncStatusChoices.HasWaitingItems;
  // A report already working on its goals isn't sent to the client's first
  // review: it gets a banner instead. A report with no goals yet reviews first,
  // once its goals have loaded or failed to (cached goals still count).
  const reportHasGoals = !!data?.reassessmentEhrGoals?.some((g) => g?.reportGoalCreated);
  const reviewFirst = awaitingReview && !reportHasGoals && (!isFetching || isError);
  const reviewLater = learningTreeReviewEnabled && awaitingReview && reportHasGoals;
  // Until we know this report keeps its goals, show the loader rather than a
  // goal list the redirect would replace.
  const holdForReview = learningTreeReviewEnabled && awaitingReview && !reportHasGoals;
  useEffect(() => {
    if (learningTreeReviewEnabled && reviewFirst && isSyncStatusFresh) {
      reviewRedirectRef.current = true;
      void navigate("../review-learning-tree", { replace: true, state: locationState });
    }
  }, [learningTreeReviewEnabled, reviewFirst, isSyncStatusFresh, navigate, locationState]);
  // A client whose structure is locked can unlock a branch to change it.
  const { data: learningTreeData } = useCrLearningTreeQuery(
    { learnerId, reportId: reportId || undefined },
    { enabled: learningTreeReviewEnabled && !!learnerId, refetchOnWindowFocus: false },
  );
  const canEditLearningTree =
    learningTreeReviewEnabled &&
    !hasWaitingItems &&
    !awaitingReview &&
    !!learningTreeData?.crLearningTree?.nodes?.some((n) => n.locked);

  const tabs = [
    { key: "all" as const, label: "All goals", count: totalGoalCount > 100 ? "100+" : String(totalGoalCount) },
    { key: "selected" as const, label: "Selected", count: String(selectedGoalCount) },
  ];

  if (isLoading || holdForReview) {
    return (
      <div className="flex items-center justify-center bg-limestone-50 min-h-full">
        <div className="w-full flex items-center justify-center max-w-[856px] bg-white rounded-lg shadow-xs my-8 mx-4 p-8">
          <Loader className="min-h-[400px] text-text-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center bg-limestone-50 min-h-full">
      <div className="w-full max-w-[856px] bg-white rounded-lg shadow-xs my-8 mx-4 flex flex-col">
        {/* Header */}
        <div className="px-8 pt-8">
          <div className="flex items-center justify-between gap-4">
            <Headline size="medium">Manage EHR Goals & Targets</Headline>
            {canEditLearningTree && (
              <Button
                text="Edit learning tree"
                appearance="text"
                onClick={() => void navigate("../review-learning-tree", { state: locationState })}
              />
            )}
          </div>
          <Small colorType="secondary" className="mt-2 block">
            Add or remove all the relevant EHR programming for this report.
          </Small>
        </div>

        {hasWaitingItems && (
          <div className="px-8 mt-4 flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 mx-8 py-3">
            <Small>
              New items in CentralReach wait for your review. They aren&apos;t imported until you lock them.
            </Small>
            <Button
              text="Review new items"
              appearance="text"
              onClick={() => void navigate("../review-learning-tree", { state: locationState })}
            />
          </div>
        )}

        {reviewLater && (
          <div className="px-8 mt-4 flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 mx-8 py-3">
            <Small>
              This client&apos;s CentralReach learning tree is ready for review. Your goals stay as they are; other
              items aren&apos;t imported until you review and lock them.
            </Small>
            <Button
              text="Review learning tree"
              appearance="text"
              onClick={() =>
                void navigate("../review-learning-tree", { state: { ...locationState, fromSelectProgramming: true } })
              }
            />
          </div>
        )}

        {/* Tabs */}
        <div className="px-8 mt-4">
          <TabNavigation
            shrink
            tabs={tabs.map((tab) => ({
              current: activeTab === tab.key,
              type: "component" as const,
              name: tab.label,
              Component: (
                <Button
                  text={
                    <div className="flex items-center gap-2 font-normal">
                      {tab.label}
                      <Badge text={tab.count} appearance="neutral" />
                    </div>
                  }
                  appearance="text"
                  className={`hover:text-primary ${activeTab === tab.key ? "text-limestone-900" : "text-text-secondary"}`}
                  onClick={() => setActiveTab(tab.key)}
                />
              ),
            }))}
          />
        </div>

        {/* Filter bar — hidden on "Selected" tab */}
        {activeTab === "all" && (
          <div className="px-8 mt-4">
            <SelectProgrammingFilterBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onDateRangeFilterChange={setDateRangeFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
              onStatusFilterChange={setStatusFilter}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </div>
        )}

        {/* Goal list */}
        <div className="px-8 flex-1 overflow-y-auto pb-4">
          {filteredGoals.length > 0 && (
            <div className="mt-6 space-y-2">
              {activeTab === "selected" ? (
                <Label colorType="primary" size="medium" displayType="loud" className="flex items-center gap-2">
                  Selected to include in report{" "}
                  <Tooltip
                    text="This list reflects the goals that you have selected to be added to this report."
                    infoIconClassName="w-4! h-4!"
                  />
                </Label>
              ) : (
                <Label colorType="primary" size="medium" displayType="loud" className="flex items-center gap-2">
                  All EHR Goals{" "}
                  <Tooltip
                    text="This list reflects all active goals pulled directly from the patient's Electronic Health Record (EHR)."
                    infoIconClassName="w-4! h-4!"
                  />
                </Label>
              )}
              <div className="flex items-center justify-between">
                {activeTab === "all" && (
                  <div className="flex items-center gap-2 py-2">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allVisibleChecked}
                      onChange={() => {
                        // "Select all" bypasses each row's own onToggleGoal
                        // handler (EhrGoalRow.handleToggleGoal), so it must
                        // capture ehr_goal_toggled itself for every goal it
                        // actually flips — otherwise a bulk selection change
                        // is invisible to the same funnel a single click would
                        // have reported. Restrict to goals whose checked value
                        // actually changes (disabled goals are skipped by the
                        // reducer, and some may already match nextChecked).
                        const nextChecked = !allVisibleChecked;
                        selectableFiltered
                          .filter((goal) => goal.checked !== nextChecked)
                          .forEach((goal) => captureGoalToggled(goal.goalId, nextChecked));
                        toggleGoals(
                          filteredGoals.map((g) => g.goalId),
                          nextChecked,
                          { cascadeTargets: true },
                        );
                      }}
                      disabled={selectableFiltered.length === 0}
                      aria-label="Select all visible goals"
                      className="h-4 w-4 shrink-0 rounded-sm border-limestone-300 text-green-700 focus:ring-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <span className="flex-1 text-sm text-text-primary">Select all</span>
                  </div>
                )}

                <Label
                  colorType="primary"
                  size="extra-small"
                  className="ml-auto flex items-center gap-2"
                  displayType="loud"
                >
                  EHR Status{" "}
                  <Tooltip
                    text="The current EHR programming status of this goal. To update it, make changes directly in your EHR. EHR statuses are not included in report exports, only report statuses will appear in exports."
                    infoIconClassName="w-4! h-4!"
                  />
                </Label>
              </div>
              <div className="divide-y divide-limestone-100">
                {filteredGoals.map((goal) => (
                  <div
                    key={goal.goalId}
                    ref={(el) => {
                      if (el) goalRowRefs.current.set(goal.goalId, el);
                      else goalRowRefs.current.delete(goal.goalId);
                    }}
                  >
                    <EhrGoalRow
                      goal={goal}
                      onToggleGoal={() => toggleGoals([goal.goalId], !goal.checked)}
                      onToggleTarget={(targetId) => toggleTarget(targetId)}
                      onToggleExpand={() => toggleExpandGoal(goal.goalId)}
                      showTargets={!isGasEnabled}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {state.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 text-limestone-500">
              No goals found for this client.
              {/* The EHR import only starts on the refreshing-data step, and no
                  report status routes back there once it is left without an
                  import (e.g. a first EHR login cancelled mid-way). Without
                  this the report has no way to pull its EHR goals. */}
              {canRefreshFromEhr && (
                <Button
                  text="Refresh from EHR"
                  variant="primary"
                  buttonStyle="border"
                  onClick={() => {
                    captureEhrRefreshClicked("select_programming_empty");
                    void navigate("../refreshing-data", { state: locationState });
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-limestone-100 px-8 py-4 flex items-center justify-end gap-4 rounded-b-lg">
          <Small colorType="secondary">{selectedGoalCount} goals selected</Small>
          <Button
            text="Update"
            appearance="primary"
            onClick={handleUpdate}
            disabled={selectedGoalCount === 0 || isCreatingGoals}
          />
        </div>
      </div>

      {blocker.state === "blocked" && (
        <Dialog
          title="Unsaved changes"
          open={true}
          setOpen={() => blocker.reset?.()}
          primaryButton={{ text: "Leave anyway", onClick: () => blocker.proceed?.() }}
          secondaryButton={{ text: "Stay", onClick: () => blocker.reset?.() }}
          size="sm"
        >
          <Paragraph>You have unsaved changes. Leave without updating?</Paragraph>
        </Dialog>
      )}
    </div>
  );
};
